/// <amd-module name="Build/Common/Artifacts/Store" />
import { Store } from "Build/Flux/Store";
import { ArtifactsActionCreator, ArtifactsActionHub } from 'Action';
import { PermissionsStore } from "Build/Common/Library/Stores/Permissions";
import { Security } from "Build/Common/Library/Security";
import { IBuildRestClient } from 'Build/Client/RestClient/Build';
import { IVssPageContext } from 'VSS/Platform/Context';
import { BuildArtifact, Build } from 'Build/Client/Contracts/Build';
import { IArtifactProvider, ArtifactProviderContext } from 'Common';
import { IArtifactProviderService } from 'providers/ArtifactProvider';
import { ArtifactsSource } from 'Source';

export interface IArtifactsStoreOptions {
    pageContext: IVssPageContext;
    projectId: string;
    buildId?: number;
    actionCreator?: ArtifactsActionCreator;
    actionHub?: ArtifactsActionHub;
    permissionsStore?: PermissionsStore;
}

export class ArtifactsStore extends Store {
    public static key = "build-artifacts-store";

    private _actionCreator: ArtifactsActionCreator;
    private _actionHub: ArtifactsActionHub;
    private _buildClient: IBuildRestClient;
    private _projectId: string;

    private _permissionsStore: PermissionsStore;
    private _security: Security;

    private _buildId: number | undefined;
    private _artifacts: BuildArtifact[] = [];
    private _artifactProviders: IArtifactProvider[] = [];

    private _fetchingArtifacts: { [key: string]: boolean } = {};
    private _providerService: IArtifactProviderService;

    constructor(options: IArtifactsStoreOptions) {
        super();
        this._buildClient = options.pageContext.getRestClient<IBuildRestClient>("IBuildRestClient");
        this._providerService = options.pageContext.getService<IArtifactProviderService>("IArtifactProviderService");
        this._actionHub = options.actionHub || new ArtifactsActionHub();
        this._projectId = options.projectId;
        this._actionCreator = options.actionCreator || new ArtifactsActionCreator({
            actionHub: this._actionHub,
            client: this._buildClient,
            projectId: this._projectId
        });

        this._permissionsStore = options.permissionsStore ? options.permissionsStore : new PermissionsStore({
            pageContext: options.pageContext
        });
        this._security = new Security({
            projectId: this._projectId,
            store: this._permissionsStore
        });

        this._actionHub.artifactsAvailable.addListener(this._setArtifacts);
        this._actionHub.artifactAdded.addListener(this._onArtifactAdded);
        this._permissionsStore.addListener(this._storeChanged);

        this._buildId = options.buildId;
    }

    public dispose() {
        this._actionHub.artifactsAvailable.removeListener(this._setArtifacts);
        this._actionHub.artifactAdded.removeListener(this._onArtifactAdded);
        this._permissionsStore.removeListener(this._storeChanged);
        this._fetchingArtifacts = {};
    }

    public getArtifactProviders() {
        return this._artifactProviders;
    }

    public canExploreArtifacts() {
        return !(this._security.isAnonymousUser() || this._security.isPublicUser());
    }

    public addArtifact(buildId: number, name: string) {
        if (this._buildId === buildId && !this._fetchingArtifacts[name]) {
            this._actionCreator.fetchArtifact(buildId, name);
            this._fetchingArtifacts[name] = true;
        }
    }

    public updateBuild(buildId: number | undefined) {
        if (this._buildId !== buildId) {
            this._buildId = buildId;
            if (!this._buildId) {
                this._setArtifacts([]);
            }
            else {
                this._actionCreator.fetchArtifacts(this._buildId);
            }
        }
    }

    private _setArtifacts = (artifacts: BuildArtifact[]) => {
        this._artifacts = artifacts;

        const providerContext = {
            buildId: this._buildId,
            projectId: this._projectId
        } as ArtifactProviderContext;

        this._artifactProviders = (artifacts || []).map(x => this._providerService.getArtifactProvider(x, providerContext));
        this._storeChanged();
    }

    private _onArtifactAdded = (artifact: BuildArtifact) => {
        this._artifacts.push(artifact);
        this._artifacts = ArtifactsSource.sortArtifacts(this._artifacts);
        this._setArtifacts(this._artifacts);
    }

    private _storeChanged = () => {
        this.emitChanged();
    }
}
